/**
 * @file fileSave.ts
 * Environment-agnostic "save file to disk". A host can replace the browser
 * download with its own native dialog through the `fileSave` section of its
 * host registration (see host.ts; e.g. grabcadPlugin.ts).
 *
 * saveFileAs() routes to the current env's provider, or falls back to a
 * browser <a download> when there is none. Exposed to app developers as a
 * method on <as-app>.
 */

import { internalData } from '../../store/store.ts';
import { devlog } from '../../util/utils.ts';
import { getHost, registerHost } from './host.ts';

export interface FileSaveResult {
  /** False when the user cancelled the save dialog (providers only — the
   *  browser download fallback cannot detect cancellation). */
  success: boolean;
  /** Path the file was written to, where the provider reports one. */
  filepath?: string;
}

export interface FileSaveOptions {
  /** Native dialog title, where the environment shows one. */
  title?: string;
  /** Extension for the dialog's file filter, without the dot (e.g. 'asproject'). */
  fileExtension?: string;
  /** Human-readable name for the extension filter (e.g. 'App Suite Project'). */
  fileExtensionName?: string;
}

export interface FileSaveProvider {
  /** Save the blob via the host environment's native mechanism. Resolves once
   *  the host reports the result — callers may await it before e.g. closing
   *  the window. */
  saveFile(blob: Blob, filename: string, opts: FileSaveOptions): Promise<FileSaveResult>;
  /**
   * If true, this provider accurately tracks whether the user saved or cancelled
   * (i.e. the save runs through a native host dialog that reports its result).
   * Callers should defer the actual saveFile call to the host's close sequence
   * rather than invoking it standalone — the provider requires that context to
   * show its dialog and receive a response.
   * The browser <a download> fallback leaves this unset (fire-and-forget).
   */
  saveStatusTrackable?: true;
}

/** Same as `registerHost(env, { fileSave: provider })`. */
export function registerFileSaveProvider(env: string, provider: FileSaveProvider): void {
  registerHost(env, { fileSave: provider });
}

/** Returns true if the current environment's save provider is saveStatusTrackable. */
export function isSaveTrackable(): boolean {
  return getHost()?.fileSave?.saveStatusTrackable === true;
}

/** The save provider matching the given env (defaults to the current appEnv), or
 *  undefined when none is registered (i.e. saveFileAs falls back to a browser
 *  <a download>). Lets callers tell which channel a save actually went through. */
export function getFileSaveProvider(env: string | null = internalData.appEnv): FileSaveProvider | undefined {
  return getHost(env)?.fileSave;
}

/**
 * Save a file to disk using the mechanism appropriate for the current
 * environment: the registered provider's native flow if one matches
 * internalData.appEnv, otherwise a browser <a download>.
 */
export async function saveFileAs(
  blob: Blob,
  filename: string,
  opts: FileSaveOptions = {}
): Promise<FileSaveResult> {
  const provider = getFileSaveProvider();
  devlog('[SAVE] saveFileAs: appEnv =', internalData.appEnv ?? '(none)', '| provider =', !!provider, '| file =', filename);
  if (provider) return provider.saveFile(blob, filename, opts);

  const url = URL.createObjectURL(blob);
  Object.assign(document.createElement('a'), { href: url, download: filename }).click();
  URL.revokeObjectURL(url);
  return { success: true };
}
